import { api } from "@repo/convex/api";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useOrganization } from "@/contexts/organization-context";
import { SettingsPageShell } from "@/features/settings";
import { useObservedMutation } from "@/hooks/use-observed-convex";

function GeneralOrganizationSettingsPage() {
  const { organization, organizationId } = useOrganization();
  const updateOrganization = useObservedMutation(api.organizations.updateSettings, {
    feature: "settings",
    operation: "update-organization",
    includeRequestId: true,
  });
  const guidance = useQuery(
    api.assistantOrgGuidance.get,
    organizationId ? { orgId: organizationId } : "skip"
  );
  const updateGuidance = useObservedMutation(api.assistantOrgGuidance.update, {
    feature: "settings",
    operation: "update-assistant-org-guidance",
    includeRequestId: true,
  });

  const [name, setName] = useState(organization?.name ?? "");
  const [logoUrl, setLogoUrl] = useState(organization?.logo ?? "");
  const [guidanceText, setGuidanceText] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (guidance) setGuidanceText(guidance.text);
  }, [guidance]);

  const handleSave = async () => {
    if (!organizationId) return;
    await updateOrganization({
      orgId: organizationId,
      name,
      logo: logoUrl || undefined,
    });
    if (guidance?.canManage) {
      await updateGuidance({ orgId: organizationId, text: guidanceText });
    }
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1500);
  };

  return (
    <SettingsPageShell
      title="General"
      description="Update organization basics such as name, branding, and default workspace behavior."
    >
      <div className="space-y-4 max-w-xl">
        <div className="space-y-2">
          <Label htmlFor="orgName">Organization name</Label>
          <Input id="orgName" value={name} onChange={(event) => setName(event.target.value)} />
        </div>

        {guidance?.canManage ? (
          <div className="space-y-2 border-t pt-5">
            <Label htmlFor="claudiaOrgGuidance">Claudia organization guidance</Label>
            <p className="text-xs text-muted-foreground">
              Versioned administrator guidance applied to Claudia in every project. It cannot
              override platform safety or permissions.
            </p>
            <Textarea
              id="claudiaOrgGuidance"
              value={guidanceText}
              onChange={(event) => setGuidanceText(event.target.value)}
              maxLength={guidance.maxChars}
              rows={7}
              placeholder="Describe organization-wide terminology, tone, and working conventions."
            />
            <p className="text-right text-xs text-muted-foreground">
              {guidanceText.length}/{guidance.maxChars} · version {guidance.versionNumber}
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="orgLogo">Logo URL</Label>
          <Input
            id="orgLogo"
            value={logoUrl}
            onChange={(event) => setLogoUrl(event.target.value)}
            placeholder="https://example.com/logo.png"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={!organizationId}>
            Save organization settings
          </Button>
          {saved && <p className="text-sm text-muted-foreground">Organization settings saved.</p>}
        </div>
      </div>
    </SettingsPageShell>
  );
}

export const Route = createFileRoute("/settings/general")({
  component: GeneralOrganizationSettingsPage,
});
